import React, { createContext, useContext } from "react";
import { createPortal } from "react-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";

/**
 * The page title and its actions live in the top bar (next to the avatar) instead of taking a row
 * of their own. PortalLayout provides the top-bar slot; a page renders <PageHeader> anywhere and
 * it's moved up there. Outside PortalLayout (tests, bare pages) it renders in place.
 */
export interface PageHeaderSlot {
  /** True inside PortalLayout, even before the top bar has mounted. */
  inLayout: boolean;
  slot: HTMLElement | null;
}

export const PageHeaderContext = createContext<PageHeaderSlot>({ inLayout: false, slot: null });

interface Props {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Small icon/tooltip shown after the title. */
  titleAdornment?: React.ReactNode;
  /** Buttons shown on the right, just left of the avatar. */
  actions?: React.ReactNode;
}

const PageHeader = ({ title, subtitle, titleAdornment, actions }: Props): JSX.Element | null => {
  const { inLayout, slot } = useContext(PageHeaderContext);

  const content = (
    <Box
      sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2, width: "100%", minWidth: 0 }}
      data-sentry-unmask
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{
            display: "flex",
            alignItems: "center",
            fontSize: 18,
            fontWeight: 500,
            lineHeight: 1.3,
            color: "text.primary",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {title}
          {titleAdornment}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 13, color: "text.secondary", whiteSpace: "nowrap" }}>{subtitle}</Typography>
        )}
      </Box>
      {actions && <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>{actions}</Box>}
    </Box>
  );

  if (!inLayout) return content;
  // Wait for the top bar instead of flashing the header in the page body first.
  return slot ? createPortal(content, slot) : null;
};

export default PageHeader;
